import {BaseApi} from './BaseApi'
import {RoleCategory} from './roleApi'
import {reportCatchError} from '../utils/errorReporter'

// ============================================
// MODULE CONFIGURATION INTERFACES
// ============================================

/**
 * Module definition - can be a top-level module, submodule, or nested submodule.
 * Structure is recursive: submodules can contain further submodules.
 *
 * Example from API:
 * - global (module)
 *   - roles_permissions (submodule)
 *     - roles_platform (sub-submodule)
 *     - roles_msp (sub-submodule)
 */
export interface ModuleDefinition {
  key: string
  name: string
  description?: string
  availableOperations?: string[]
  /**
   * A grant only its own checkbox may change (employee_metrics_financial). The editors honour it
   * on a submodule at any depth; see isSensitive in Roles_And_Permissions/utils/permissionTree.
   */
  sensitive?: boolean
  /**
   * The role editor starts a brand-new role with this node, and everything beneath it, unticked
   * instead of ticked; it can still be ticked. Set per category by the catalogue (STFFLG-4108:
   * Importers and Tax Credits outside Platform). See isOffByDefault in permissionTree.
   */
  offByDefault?: boolean
  submodules?: ModuleDefinition[]
}

/**
 * @deprecated Use ModuleDefinition instead - submodules have same structure as modules
 */
export type SubmoduleDefinition = ModuleDefinition

export interface CategoryModuleConfig {
  category: RoleCategory
  modules: ModuleDefinition[]
}

// ============================================
// MODULE API SERVICE
// ============================================

export class ModuleApiService extends BaseApi {
  protected modulesPath = '/modules'

  /**
   * Get all modules organized by category
   * GET /modules
   */
  async getAllModules(): Promise<CategoryModuleConfig[]> {
    try {
      const response = await this.client.get<CategoryModuleConfig[]>(this.modulesPath)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all modules:', error)
      throw error
    }
  }

  /**
   * Get available role categories
   * GET /modules/categories
   */
  async getCategories(): Promise<RoleCategory[]> {
    try {
      const response = await this.client.get<RoleCategory[]>(`${this.modulesPath}/categories`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching categories:', error)
      throw error
    }
  }

  /**
   * Get modules for a specific category
   * GET /modules/category/:category
   */
  async getModulesByCategory(category: RoleCategory): Promise<ModuleDefinition[]> {
    try {
      const response = await this.client.get<ModuleDefinition[]>(
        `${this.modulesPath}/category/${category}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching modules for category:', error)
      throw error
    }
  }

  /**
   * Get flattened module keys for a category
   * GET /modules/keys/:category
   */
  async getModuleKeysForCategory(category: RoleCategory): Promise<string[]> {
    try {
      const response = await this.client.get<string[]>(`${this.modulesPath}/keys/${category}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching module keys for category:', error)
      throw error
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Get all module keys across all categories (flattened)
   */
  async getAllModuleKeys(): Promise<string[]> {
    try {
      const allModules = await this.getAllModules()
      return this.extractModuleKeys(allModules)
    } catch (error: unknown) {
      reportCatchError('Error fetching all module keys:', error)
      throw error
    }
  }

  /**
   * Extract module keys from category configs (helper to avoid deep nesting)
   */
  private extractModuleKeys(categoryConfigs: CategoryModuleConfig[]): string[] {
    const keys: string[] = []

    for (const categoryConfig of categoryConfigs) {
      for (const module of categoryConfig.modules) {
        this.collectModuleKeys(module, keys)
      }
    }

    return keys
  }

  /**
   * Recursively collect all keys from a module and its submodules
   */
  private collectModuleKeys(module: ModuleDefinition, keys: string[]): void {
    keys.push(module.key)
    if (module.submodules) {
      for (const submodule of module.submodules) {
        this.collectModuleKeys(submodule, keys)
      }
    }
  }

  /**
   * Get module definition by key
   */
  async getModuleByKey(
    category: RoleCategory,
    moduleKey: string,
  ): Promise<ModuleDefinition | undefined> {
    try {
      const modules = await this.getModulesByCategory(category)
      return modules.find((m) => m.key === moduleKey)
    } catch (error: unknown) {
      reportCatchError('Error fetching module by key:', error)
      throw error
    }
  }

  /**
   * Check if a module key exists in a category
   */
  async moduleKeyExists(category: RoleCategory, moduleKey: string): Promise<boolean> {
    try {
      const keys = await this.getModuleKeysForCategory(category)
      return keys.includes(moduleKey)
    } catch (error: unknown) {
      reportCatchError('Error checking if module key exists:', error)
      throw error
    }
  }
}

// Export singleton instance
export const moduleApi = new ModuleApiService()
